import 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { HelmetProvider } from 'react-helmet-async'
import { ErrorBoundary } from 'react-error-boundary'
import { useScreenSize, useDeprecatedScreenSize } from '../hooks/useScreenSize'
import { devLog } from '@methods/devLog'
import { getUrlParam } from '@methods/urlParams'
import { getPathSegments } from '@methods/urlPath'
import getUserAgent from '@methods/getUserAgent'
import { Language } from '../types/translations.generated'
import { rootStore, StoreContext } from '../store/Root.store'
import { ScenarioNames } from '../store/Scenario.store'
import { ContentRouter } from './ContentRouter/ContentRouter'
import { axiosInstance } from '@methods/axiosConfig'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { Helmet } from './Helmet'
import { API_URL, ONBOARDING_SERVICE_PATH } from '@/constants/apiUrl'
import { ErrorFallback } from './ErrorCatching/ErrorFallback'
import { actionReport } from '@/methods/actionReport'
import { forceRedirect } from '@/methods/forceRedirect'
import { applyTheme } from '@/themes/apply-theme'

// Global CSS (static, CSP-compliant)
import '@/themes/index.css'
import '@/styles/backgrounds.css'
import '@/styles/docusign.css'
import '@/styles/dtp.css'
import '@/styles/eid-global.css'
import '@/styles/eid.css'
import '@/styles/eidMethodSelection.css'
import '@/styles/elavon.css'
import '@/styles/enpal.css'
import '@/styles/fonts.css'
import '@/styles/forms.css'
import '@/styles/generic.css'
import '@/styles/global.css'
import '@/styles/gocardlessTransactionDataSelection.css'
import '@/styles/gocardlessIncomeConfirmation.css'
import '@/styles/walletVerification.css'
import '@/styles/ikano.css'
import '@/styles/amex.css'
import '@/styles/layout.css'
import '@/styles/reset.css'
import '@/styles/xs2aForm.css'
import { isLocalDev } from '@/constants/envs'

export const App: FC = observer(() => {
  const [electronicIdUrl, setElectronicIdUrl] = useState('')

  const isVisible = usePageVisibility()
  const screenSize = useScreenSize()

  const path = getPathSegments().join('_')

  const deprecatedScreenSize = useDeprecatedScreenSize()

  useEffect(() => {
    handleInit()
    const UserAgent = getUserAgent()
    rootStore.AppState.setUserAgent(UserAgent)

    return () => {
      rootStore.AppState.setShouldRunHandleVisibilityChange(false)
    }
  }, [])

  useEffect(() => {
    if (
      !isVisible &&
      rootStore.InterfaceState.sendBeforeunloadEvent &&
      rootStore.AppState.shouldRunHandleVisibilityChange
    ) {
      navigator.sendBeacon(
        `${API_URL}/${ONBOARDING_SERVICE_PATH}/audit-log-gateway`,
        JSON.stringify({
          type: 'event.onboarding-web.process.TAB_CLOSE_REQUESTED',
          payload: {},
        })
      )
    }
  }, [isVisible])

  const getVideoIdContext = async (): Promise<void> => {
    try {
      const res = await axiosInstance.get(`${API_URL}/electronic-id/get-context`, {
        withCredentials: true,
      })

      const {
        configurationId,
        shouldAskForEmail,
        shouldAskForPhoneNumber,
        shouldSelectDocument,
        askUserForPlaceAndCountryOfBirth,
        showVideoIdHintsScreen,
        electronicIdUrl: fetchedElectronicIdUrl,
        language,
      } = res.data

      if (shouldAskForEmail) {
        rootStore.AppState.setShouldAskForEmail(shouldAskForEmail)
      }

      if (shouldAskForPhoneNumber) {
        rootStore.AppState.setShouldAskForPhoneNumber(shouldAskForPhoneNumber)
      }

      if (shouldSelectDocument) {
        rootStore.AppState.setShouldSelectDocument(shouldSelectDocument)
      }

      if (askUserForPlaceAndCountryOfBirth) {
        rootStore.AppState.setAskUserForPlaceAndCountryOfBirth(askUserForPlaceAndCountryOfBirth)
      }

      if (showVideoIdHintsScreen) {
        rootStore.AppState.setShowVideoIdHintsScreen(showVideoIdHintsScreen)
      }

      if (fetchedElectronicIdUrl) {
        setElectronicIdUrl(fetchedElectronicIdUrl)
      }

      if (language) {
        rootStore.TranslationsState.setTranslations(language, configurationId)
      }
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    if ('videoId' === path || 'videoIdForStandardSignature' === path) {
      getVideoIdContext()
    }
  }, [])

  const handleInit = async () => {
    try {
      // language
      const language = getUrlParam('language')
      devLog('language: ', language)
      if (language) {
        rootStore.TranslationsState.setTranslations(language as Language)
      }

      // scenario
      devLog('path: ', path)
      if (path) rootStore.ScenarioState.init(path as ScenarioNames)
    } catch (e) {
      devLog(e)
    }
  }

  // Apply CSS custom properties theme when themeId changes
  useEffect(() => {
    applyTheme(rootStore.InterfaceState.themeId)
  }, [rootStore.InterfaceState.themeId])

  // Toggle iframe background override (replaces DocumentStyle bgColor prop)
  useEffect(() => {
    document.body.classList.toggle('iframe-content', rootStore.AppState.iframeContent)
  }, [rootStore.AppState.iframeContent])

  useEffect(() => {
    rootStore.AppState.setScreenSize(screenSize)
    rootStore.AppState.setInitialScreenSize(screenSize)
  }, [screenSize])

  useEffect(() => {
    rootStore.AppState.setDeprecatedScreenSize(deprecatedScreenSize)
  }, [deprecatedScreenSize])

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        devLog('Error caught by boundary:', error, errorInfo)

        if (isLocalDev) {
          return
        }

        actionReport({
          type: 'event.onboarding-web.process.REACT_ERROR_BOUNDARY_CATCH',
          payload: {
            error: {
              message: error.message,
              stack: error.stack,
              name: error.name,
            },
            componentStack: errorInfo.componentStack,
            userAgent: navigator.userAgent,
          },
        })

        forceRedirect(
          `${API_URL}/browser-not-supported?language=${getUrlParam('language') || 'en'}`
        )
      }}
    >
      <StoreContext.Provider value={rootStore}>
        <HelmetProvider>
          <Helmet electronicIdUrl={electronicIdUrl} />
          <main className="App">
            <ContentRouter />
          </main>
        </HelmetProvider>
      </StoreContext.Provider>
    </ErrorBoundary>
  )
})
